import { useForm } from 'react-hook-form';
import { useEffect, useState, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { useFormStore, useNavigation } from '../store/formStore';
import {
  InsuranceCode,
  ALL_INSURANCE_CODES,
  INSURANCE_DESCRIPTIONS,
  getInsuranceOptions,
  getDefaultInsuranceCode,
  getAvailableInsuranceCodes,
} from '../utils/insurance';

// Bulle d'explication pour les acronymes : hover sur desktop, clic sur mobile
function Abbr({ term, definition }: { term: string; definition: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-block">
      <button
        type="button"
        className="border-b border-dotted border-neutral-500 cursor-help font-medium"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(v => !v)}
        aria-label={`${term} : ${definition}`}
      >
        {term}
      </button>
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs bg-neutral-800 text-white rounded-md whitespace-nowrap z-20 shadow-lg pointer-events-none">
          {definition}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-800" />
        </span>
      )}
    </span>
  );
}

// Label JSX avec acronymes cliquables/survolables
function InsuranceLabel({ value }: { value: InsuranceCode }) {
  const ptia = <Abbr term="PTIA" definition="Perte Totale et Irréversible d'Autonomie" />;
  const itt  = <Abbr term="ITT"  definition="Incapacité Temporaire Totale de travail" />;
  const pe   = <Abbr term="PE"   definition="Perte d'Emploi" />;

  switch (value) {
    case 'DC PTIA /IT /PE': return <span>Décès + {ptia} + {itt} + {pe}</span>;
    case 'DC PTIA /IT':     return <span>Décès + {ptia} + {itt}</span>;
    case 'DC':              return <span>Décès uniquement</span>;
    case 'N':               return <span>Aucune assurance</span>;
  }
}

// Schéma Zod : accepte tous les codes Annexe K + 'N'
const ALL_VALUES = [...ALL_INSURANCE_CODES, 'N'] as [InsuranceCode, ...InsuranceCode[]];

const insuranceSchema = z.object({
  borrowerInsurance: z.enum(ALL_VALUES, {
    errorMap: () => ({ message: 'Veuillez sélectionner une option' }),
  }),
  coBorrowerInsurance: z.enum(ALL_VALUES, {
    errorMap: () => ({ message: 'Veuillez sélectionner une option' }),
  }).optional(),
});

interface InsuranceFormData {
  borrowerInsurance: InsuranceCode;
  coBorrowerInsurance?: InsuranceCode;
}

export function InsuranceStep() {
  const { formData, updateField } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();

  const hasCoBorrower = formData.hasCoBorrower === true;

  // Options disponibles selon l'âge (< 65 ans → DC PTIA /IT /PE ou DC PTIA /IT ; ≥ 65 ans → aucune)
  const borrowerOptions    = getInsuranceOptions(formData.birthDate);
  const coBorrowerOptions  = getInsuranceOptions(formData.coBorrowerBirthDate);

  // Défauts adaptatifs
  const borrowerDefault   = getDefaultInsuranceCode(formData.birthDate);
  const coBorrowerDefault = getDefaultInsuranceCode(formData.coBorrowerBirthDate);

  // Valeur initiale : si déjà en store et compatible → la garder, sinon défaut adaptatif
  const borrowerAvailable   = getAvailableInsuranceCodes(formData.birthDate);
  const coBorrowerAvailable = getAvailableInsuranceCodes(formData.coBorrowerBirthDate);

  const resolveInitial = (
    stored: any,
    available: InsuranceCode[],
    defaultCode: InsuranceCode
  ): InsuranceCode => {
    if (stored === null) return 'N';
    if (stored && available.includes(stored as InsuranceCode)) return stored as InsuranceCode;
    return defaultCode;
  };

  const initialBorrower    = resolveInitial(formData.borrowerInsurance,    borrowerAvailable,   borrowerDefault);
  const initialCoBorrower  = resolveInitial(formData.coBorrowerInsurance,  coBorrowerAvailable, coBorrowerDefault);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm<InsuranceFormData>({
    resolver: zodResolver(insuranceSchema),
    defaultValues: {
      borrowerInsurance:   initialBorrower,
      coBorrowerInsurance: hasCoBorrower ? initialCoBorrower : undefined,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    trigger();
    // Synchroniser le store avec les valeurs initiales résolues
    updateField('borrowerInsurance', initialBorrower !== 'N' ? initialBorrower : null);
    updateField('subscription', initialBorrower !== 'N');
    if (hasCoBorrower) {
      updateField('coBorrowerInsurance', initialCoBorrower !== 'N' ? initialCoBorrower : null);
    }
  }, []);

  const fieldError = (name: keyof InsuranceFormData) =>
    touchedFields[name] ? formErrors[name]?.message : undefined;

  const watchedBorrowerInsurance   = watch('borrowerInsurance');
  const watchedCoBorrowerInsurance = watch('coBorrowerInsurance');

  const onSubmit = (data: InsuranceFormData) => {
    updateField('borrowerInsurance', data.borrowerInsurance !== 'N' ? data.borrowerInsurance : null);
    if (data.coBorrowerInsurance !== undefined) {
      updateField('coBorrowerInsurance', data.coBorrowerInsurance !== 'N' ? data.coBorrowerInsurance : null);
    }
    const subscription =
      data.borrowerInsurance !== 'N' ||
      (data.coBorrowerInsurance !== undefined && data.coBorrowerInsurance !== 'N');
    updateField('subscription', subscription);
    goToNextStep();
  };

  const handleInsuranceChange = (fieldName: keyof InsuranceFormData) => (value: string) => {
    setValue(fieldName, value as InsuranceCode, { shouldValidate: true, shouldTouch: true });
    updateField(fieldName, value !== 'N' ? value : null);
  };

  // < 65 ans → formules disponibles ; ≥ 65 ans → aucune assurance
  const canHaveInsurance = borrowerAvailable.some(c => c !== 'N');

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Assurance emprunteur</CardTitle>
          <CardDescription>
            L'assurance protège vous et vos proches en cas d'imprévu
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {/* Assurance emprunteur principal */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900">
                Assurance pour l'emprunteur principal
              </h3>

              {canHaveInsurance && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-primary-800">
                    <strong>Couverture recommandée :</strong>{' '}
                    Décès,{' '}
                    <Abbr term="PTIA" definition="Perte Totale et Irréversible d'Autonomie" />,{' '}
                    <Abbr term="ITT" definition="Incapacité Temporaire Totale de travail" />,{' '}
                    <Abbr term="PE" definition="Perte d'Emploi" />
                  </p>
                </div>
              )}

              {!canHaveInsurance ? (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                  <p className="text-sm text-neutral-700">
                    L'assurance emprunteur n'est pas disponible pour les personnes de 75 ans et plus.
                    Votre dossier sera soumis sans assurance.
                  </p>
                </div>
              ) : (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Souhaitez-vous souscrire à l'assurance emprunteur ?
                  <span className="form-label-required">*</span>
                </label>

                <div className="space-y-3">
                  {borrowerOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        watchedBorrowerInsurance === option.value
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <input
                        type="radio"
                        {...register('borrowerInsurance')}
                        value={option.value}
                        onChange={() => handleInsuranceChange('borrowerInsurance')(option.value)}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-neutral-900">
                          <InsuranceLabel value={option.value} />
                        </div>
                        <div className="text-sm text-neutral-600 mt-1">
                          {INSURANCE_DESCRIPTIONS[option.value]}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {fieldError('borrowerInsurance') && (
                  <p className="error-message mt-2">{fieldError('borrowerInsurance')}</p>
                )}
              </div>
              )}
            </div>

            {/* Assurance co-emprunteur (si applicable) */}
            {hasCoBorrower && (
              <div className="space-y-4 pt-6 border-t border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Assurance pour le co-emprunteur
                </h3>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    Le co-emprunteur souhaite-t-il souscrire à l'assurance ?
                    <span className="form-label-required">*</span>
                  </label>

                  <div className="space-y-3">
                    {coBorrowerOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          watchedCoBorrowerInsurance === option.value
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <input
                          type="radio"
                          {...register('coBorrowerInsurance')}
                          value={option.value}
                          onChange={() => handleInsuranceChange('coBorrowerInsurance')(option.value)}
                          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-neutral-900">
                            <InsuranceLabel value={option.value} />
                          </div>
                          <div className="text-sm text-neutral-600 mt-1">
                            {INSURANCE_DESCRIPTIONS[option.value]}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {fieldError('coBorrowerInsurance') && (
                    <p className="error-message mt-2">{fieldError('coBorrowerInsurance')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Information sur l'assurance choisie */}
            {((watchedBorrowerInsurance && watchedBorrowerInsurance !== 'N') ||
              (watchedCoBorrowerInsurance && watchedCoBorrowerInsurance !== 'N')) && (
              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                <h3 className="font-semibold text-success-700 mb-2">
                  ✅ Assurance souscrite
                </h3>
                <p className="text-sm text-success-700">
                  Le coût de l'assurance sera inclus dans votre mensualité. Vous recevrez un devis détaillé avec votre offre de crédit.
                </p>
              </div>
            )}

            {/* Note informative */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h3 className="font-semibold text-neutral-800 mb-2">
                ℹ️ À propos de l'assurance emprunteur
              </h3>
              <ul className="text-sm text-neutral-700 space-y-1">
                <li>• L'assurance est facultative mais fortement recommandée</li>
                <li>• Elle couvre les risques de décès, d'invalidité et d'incapacité de travail</li>
                <li>• Le coût varie selon votre âge et votre situation</li>
                <li>• Vous pouvez déléguer votre assurance (assurance externe)</li>
              </ul>
            </div>

            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={isValid}
              nextButtonText="Voir le récapitulatif"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
